import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { getBackendUrl } from "../lib/config";

const pub = axios.create();
pub.interceptors.request.use(async (config) => {
  config.baseURL = `${await getBackendUrl()}/api`;
  return config;
});

// ── Helpers ──────────────────────────────────────────────
const fmtTime = (iso) => {
  const t = iso.split("T")[1] || iso;
  const [h, m] = t.split(":").map(Number);
  const period = h >= 12 ? "PM" : "AM";
  return `${h % 12 || 12}:${String(m).padStart(2, "0")} ${period}`;
};

const fmtDate = (dateStr) => {
  const [y, mo, d] = dateStr.split("-").map(Number);
  const dt = new Date(y, mo - 1, d);
  return dt.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
};

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

// Customer-facing duration string. Intentionally generic — never references
// internal scheduling vocabulary (stages, occupied, processing, etc.).
//   45  -> "45 min"
//   60  -> "1 hr"
//   75  -> "1 hr 15 min"
//   120 -> "2 hrs"
const formatPublicDuration = (mins) => {
  const m = Math.max(0, Number(mins) || 0);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  const hPart = `${h} ${h === 1 ? "hr" : "hrs"}`;
  return rem === 0 ? hPart : `${hPart} ${rem} min`;
};

// ── Simple Calendar Picker ────────────────────────────────
function CalendarPicker({ value, onChange }) {
  const today = new Date();
  const initMonth = value
    ? new Date(Number(value.split("-")[0]), Number(value.split("-")[1]) - 1, 1)
    : new Date(today.getFullYear(), today.getMonth(), 1);
  const [view, setView] = useState(initMonth);

  const yr = view.getFullYear();
  const mo = view.getMonth();
  const firstDow = new Date(yr, mo, 1).getDay(); // 0=Sun
  const startOffset = (firstDow + 6) % 7; // shift to Mon=0
  const daysInMonth = new Date(yr, mo + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startOffset; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const todayDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const monthLabel = view.toLocaleDateString("en-US", { month: "long", year: "numeric" });

  const cellStr = (d) =>
    `${yr}-${String(mo + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;

  const isDisabled = (d) => {
    if (!d) return true;
    return new Date(yr, mo, d) < todayDate;
  };

  const prevMonth = () => setView(new Date(yr, mo - 1, 1));
  const nextMonth = () => setView(new Date(yr, mo + 1, 1));

  return (
    <div className="w-full select-none" data-testid="public-calendar">
      <div className="flex items-center justify-between mb-4">
        <button
          onClick={prevMonth}
          className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-[#EFEBE3] transition-colors text-[#1A3626] font-bold"
          data-testid="cal-prev-month"
          aria-label="Previous month"
        >
          ‹
        </button>
        <span className="text-sm font-semibold text-[#1C201E]">{monthLabel}</span>
        <button
          onClick={nextMonth}
          className="w-8 h-8 flex items-center justify-center rounded-md hover:bg-[#EFEBE3] transition-colors text-[#1A3626] font-bold"
          data-testid="cal-next-month"
          aria-label="Next month"
        >
          ›
        </button>
      </div>
      <div className="grid grid-cols-7 gap-0.5 mb-1">
        {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((d) => (
          <div key={d} className="text-center text-[10px] font-medium text-[#5C635A] uppercase tracking-wide py-1">
            {d}
          </div>
        ))}
        {cells.map((d, i) => {
          const selected = d && cellStr(d) === value;
          const disabled = isDisabled(d);
          return (
            <button
              key={i}
              disabled={disabled}
              onClick={() => d && !disabled && onChange(cellStr(d))}
              className={`
                h-9 w-full rounded-md text-sm transition-all
                ${!d ? "invisible pointer-events-none" : ""}
                ${disabled ? "text-[#C0BCBA] cursor-not-allowed" : "cursor-pointer hover:bg-[#EFEBE3]"}
                ${selected ? "bg-[#1A3626] !text-white hover:bg-[#2B4F39] font-semibold" : "text-[#1C201E]"}
              `}
              data-testid={d ? `cal-day-${cellStr(d)}` : undefined}
            >
              {d}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// ── Step progress bar ─────────────────────────────────────
const STEPS = ["Service", "Stylist", "Date & Time", "Your Details", "Confirmed"];

function StepBar({ current }) {
  return (
    <div className="flex items-center gap-1 mb-8" data-testid="public-step-bar">
      {STEPS.map((label, i) => {
        const n = i + 1;
        const done = n < current;
        const active = n === current;
        return (
          <div key={n} className="flex items-center gap-1 flex-1 min-w-0">
            <div
              className={`flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center text-[11px] font-semibold transition-all ${
                done
                  ? "bg-[#1A3626] text-white"
                  : active
                  ? "bg-[#1A3626] text-white ring-2 ring-[#1A3626]/20"
                  : "bg-[#EFEBE3] text-[#5C635A]"
              }`}
              data-testid={`step-indicator-${n}`}
            >
              {done ? "✓" : n}
            </div>
            {i < STEPS.length - 1 && (
              <div className={`h-0.5 flex-1 rounded-full transition-all ${done ? "bg-[#1A3626]" : "bg-[#E0DBD0]"}`} />
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────
export default function PublicBookingPage() {
  const { token } = useParams();
  const [step, setStep] = useState(1);
  const [ctx, setCtx] = useState(null);
  const [pageError, setPageError] = useState(null);
  const [loading, setLoading] = useState(true);

  // Selections
  const [service, setService] = useState(null);
  const [stylistChoice, setStylistChoice] = useState(null); // null|"any"|{id,name,color}
  const [date, setDate] = useState("");
  const [slot, setSlot] = useState(null); // {start,end,stylist_id,stylist_name}
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);

  // Customer form
  const [form, setForm] = useState({ name: "", phone: "", email: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await pub.get(`/public/${token}/context`);
        setCtx(data);
      } catch (e) {
        const status = e.response?.status;
        if (status === 404) setPageError("This booking link is no longer valid.");
        else if (status === 403) setPageError(e.response?.data?.detail || "Online booking is not available.");
        else setPageError("Unable to load this booking page. Please try again.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  // Fetch slots whenever service, stylist choice, or date changes
  useEffect(() => {
    if (!service || !date || step !== 3) return;
    setSlotsLoading(true);
    setSlot(null);
    const params = {
      service_id: service.id,
      date,
      ...(stylistChoice && stylistChoice !== "any" ? { stylist_id: stylistChoice.id } : {}),
    };
    pub
      .get(`/public/${token}/availability`, { params })
      .then(({ data }) => setSlots(data))
      .catch(() => {
        setSlots([]);
        toast.error("Unable to load available times. Please try a different date.");
      })
      .finally(() => setSlotsLoading(false));
  }, [service, stylistChoice, date, step, token]);

  const submitBooking = async () => {
    if (!form.name.trim()) { toast.error("Please enter your name."); return; }
    if (!form.phone.trim()) { toast.error("Please enter your phone number."); return; }
    setSubmitting(true);
    try {
      const { data } = await pub.post(`/public/${token}/book`, {
        customer_name: form.name.trim(),
        customer_phone: form.phone.trim(),
        customer_email: form.email.trim() || undefined,
        service_id: service.id,
        stylist_id: slot.stylist_id,
        start_at: slot.start,
      });
      setConfirmation(data);
      setStep(5);
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Unable to complete booking. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // ── Page shell ─────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center">
        <div className="text-[#5C635A] text-sm animate-pulse">Loading…</div>
      </div>
    );
  }

  if (pageError) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center px-4">
        <div className="bg-white border border-[#E0DBD0] rounded-xl p-8 max-w-sm w-full text-center shadow-sm" data-testid="public-error">
          <div className="text-4xl mb-4">✦</div>
          <h2 className="font-semibold text-[#1C201E] text-lg mb-2">Booking Unavailable</h2>
          <p className="text-[#5C635A] text-sm">{pageError}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6]" data-testid="public-booking-page">
      {/* Header */}
      <header className="bg-[#1A3626] text-white px-6 py-5" data-testid="public-header">
        <div className="max-w-lg mx-auto">
          <div className="text-xs uppercase tracking-[0.2em] font-medium text-white/60 mb-0.5">Book an appointment</div>
          <h1 className="text-xl font-semibold">{ctx?.salon_name}</h1>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-8">
        <StepBar current={step} />

        {/* Step 1 — Service */}
        {step === 1 && (
          <StepSection title="Choose a service" testid="step-service">
            <div className="space-y-3">
              {(ctx?.services || []).map((svc) => (
                <button
                  key={svc.id}
                  onClick={() => { setService(svc); setStylistChoice(null); setDate(""); setSlot(null); setStep(2); }}
                  data-testid={`service-option-${svc.id}`}
                  className="w-full text-left bg-white border border-[#E0DBD0] rounded-xl p-4 hover:border-[#1A3626] hover:shadow-sm transition-all group"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="font-semibold text-[#1C201E] text-sm group-hover:text-[#1A3626] transition-colors">
                        {svc.name}
                      </div>
                      <div className="text-[#5C635A] text-xs mt-0.5">
                        {formatPublicDuration(svc.duration_minutes)}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="font-semibold text-[#1A3626] text-sm">
                        {ctx?.currency_symbol}{svc.price.toFixed(0)}
                      </div>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </StepSection>
        )}

        {/* Step 2 — Stylist */}
        {step === 2 && (
          <StepSection title="Choose a stylist" back={() => setStep(1)} testid="step-stylist">
            <div className="space-y-3">
              {/* Any stylist option */}
              <button
                onClick={() => { setStylistChoice("any"); setStep(3); }}
                data-testid="stylist-option-any"
                className={`w-full text-left bg-white border rounded-xl p-4 hover:border-[#1A3626] transition-all ${
                  stylistChoice === "any" ? "border-[#1A3626]" : "border-[#E0DBD0]"
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#EFEBE3] flex items-center justify-center text-[#1A3626] font-semibold text-sm flex-shrink-0">
                    ✦
                  </div>
                  <div>
                    <div className="font-semibold text-[#1C201E] text-sm">Any available stylist</div>
                    <div className="text-[#5C635A] text-xs">Show all available times</div>
                  </div>
                </div>
              </button>
              {/* Filter to stylists who offer this service */}
              {(ctx?.stylists || [])
                .filter((s) => {
                  if (!service?.id) return true;
                  const svcData = (ctx?.services || []).find((sv) => sv.id === service.id);
                  return true; // server will validate; show all active stylists
                })
                .map((s) => (
                  <button
                    key={s.id}
                    onClick={() => { setStylistChoice(s); setStep(3); }}
                    data-testid={`stylist-option-${s.id}`}
                    className={`w-full text-left bg-white border rounded-xl p-4 hover:border-[#1A3626] transition-all ${
                      stylistChoice?.id === s.id ? "border-[#1A3626]" : "border-[#E0DBD0]"
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-9 h-9 rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0"
                        style={{ background: s.color }}
                      >
                        {s.name.charAt(0)}
                      </div>
                      <div className="font-semibold text-[#1C201E] text-sm">{s.name}</div>
                    </div>
                  </button>
                ))}
            </div>
          </StepSection>
        )}

        {/* Step 3 — Date & Slot */}
        {step === 3 && (
          <StepSection title="Pick a date & time" back={() => setStep(2)} testid="step-datetime">
            <CalendarPicker value={date} onChange={(d) => { setDate(d); setSlot(null); }} />

            {date && (
              <div className="mt-6" data-testid="slots-section">
                <div className="text-xs uppercase tracking-wide font-medium text-[#5C635A] mb-3">
                  {fmtDate(date)}
                </div>
                {slotsLoading ? (
                  <div className="text-sm text-[#5C635A] animate-pulse py-4" data-testid="slots-loading">
                    Checking availability…
                  </div>
                ) : slots.length === 0 ? (
                  <div className="text-sm text-[#5C635A] py-4" data-testid="slots-empty">
                    No availability on this date. Try another day.
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2" data-testid="slots-grid">
                    {slots.map((s, i) => {
                      const selected = slot?.start === s.start && slot?.stylist_id === s.stylist_id;
                      return (
                        <button
                          key={`${s.start}-${s.stylist_id}`}
                          onClick={() => setSlot(s)}
                          data-testid={`slot-btn-${i}`}
                          className={`
                            px-3 py-2.5 rounded-lg border text-sm font-medium transition-all
                            ${selected
                              ? "bg-[#1A3626] text-white border-[#1A3626]"
                              : "bg-white text-[#1C201E] border-[#E0DBD0] hover:border-[#1A3626] hover:bg-[#F0F5F2]"
                            }
                          `}
                        >
                          <div>{fmtTime(s.start)}</div>
                          {stylistChoice === "any" && (
                            <div className={`text-[10px] mt-0.5 ${selected ? "text-white/70" : "text-[#5C635A]"}`}>
                              {s.stylist_name.split(" ")[0]}
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {slot && (
              <div className="mt-6">
                <Button
                  onClick={() => setStep(4)}
                  className="w-full bg-[#1A3626] hover:bg-[#2B4F39] text-white"
                  data-testid="continue-to-details-btn"
                >
                  Continue — {fmtTime(slot.start)}
                  {stylistChoice === "any" ? ` with ${slot.stylist_name}` : ""}
                </Button>
              </div>
            )}
          </StepSection>
        )}

        {/* Step 4 — Customer details */}
        {step === 4 && (
          <StepSection title="Your details" back={() => setStep(3)} testid="step-details">
            <div className="bg-[#F0F5F2] border border-[#D1E0D7] rounded-lg p-4 text-sm mb-6">
              <div className="font-medium text-[#1A3626]">{service?.name}</div>
              <div className="text-[#5C635A] text-xs mt-0.5">
                {fmtDate(date)} at {fmtTime(slot?.start || "")} · with {slot?.stylist_name}
              </div>
            </div>

            <ExpectationCard message={ctx?.customer_expectation_message} />

            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-[#5C635A]">Name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Your full name"
                  className="bg-white border-[#E0DBD0] focus:border-[#1A3626]"
                  data-testid="customer-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-[#5C635A]">Phone *</Label>
                <Input
                  type="tel"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="+1 555 000 0000"
                  className="bg-white border-[#E0DBD0] focus:border-[#1A3626]"
                  data-testid="customer-phone-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wide text-[#5C635A]">Email (optional)</Label>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="your@email.com"
                  className="bg-white border-[#E0DBD0] focus:border-[#1A3626]"
                  data-testid="customer-email-input"
                />
              </div>

              <Button
                onClick={submitBooking}
                disabled={submitting}
                className="w-full bg-[#1A3626] hover:bg-[#2B4F39] text-white mt-2"
                data-testid="submit-booking-btn"
              >
                {submitting ? "Confirming…" : "Confirm Booking"}
              </Button>
              <p className="text-center text-[11px] text-[#5C635A]">
                Your information is only used to manage your appointment.
              </p>
            </div>
          </StepSection>
        )}

        {/* Step 5 — Confirmation */}
        {step === 5 && confirmation && (
          <div className="animate-fade-in-up" data-testid="booking-confirmation">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#1A3626]/10 text-[#1A3626] text-3xl mb-4">
                ✓
              </div>
              <h2 className="text-2xl font-semibold text-[#1C201E]">You're booked!</h2>
              <p className="text-[#5C635A] text-sm mt-1">See you soon at {confirmation.salon_name}.</p>
            </div>

            <div className="bg-white border border-[#E0DBD0] rounded-xl p-5 space-y-3 shadow-sm">
              <DetailRow label="Service" value={confirmation.service_name} testid="conf-service" />
              <DetailRow label="Stylist" value={confirmation.stylist_name} testid="conf-stylist" />
              <DetailRow label="Date" value={fmtDate(confirmation.start_at.split("T")[0])} testid="conf-date" />
              <DetailRow label="Time" value={`${fmtTime(confirmation.start_at)} – ${fmtTime(confirmation.end_at)}`} testid="conf-time" />
              <DetailRow label="Duration" value={`${confirmation.duration_minutes} min`} testid="conf-duration" />
            </div>

            <ExpectationCard message={ctx?.customer_expectation_message} className="mt-6" />

            <p className="text-center text-xs text-[#5C635A] mt-6 px-4">
              Need to change or cancel? Please contact the salon directly.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────

function StepSection({ title, back, children, testid }) {
  return (
    <div className="animate-fade-in-up" data-testid={testid}>
      <div className="flex items-center gap-3 mb-5">
        {back && (
          <button
            onClick={back}
            className="flex-shrink-0 w-8 h-8 flex items-center justify-center rounded-lg bg-[#EFEBE3] hover:bg-[#E0DBD0] text-[#1A3626] transition-colors text-lg leading-none"
            data-testid="step-back-btn"
            aria-label="Go back"
          >
            ‹
          </button>
        )}
        <h2 className="text-lg font-semibold text-[#1C201E]">{title}</h2>
      </div>
      {children}
    </div>
  );
}

function DetailRow({ label, value, testid }) {
  return (
    <div className="flex items-center justify-between gap-4 py-1 border-b border-[#F0EDE7] last:border-b-0">
      <span className="text-xs uppercase tracking-wide font-medium text-[#5C635A]">{label}</span>
      <span className="text-sm font-medium text-[#1C201E]" data-testid={testid}>{value}</span>
    </div>
  );
}

// Phase 10D — salon-controlled "What to expect" reassurance card.
// Renders ONLY when the salon has populated a non-empty message.
// Plain-text rendering: React escapes content; `whitespace-pre-line` preserves
// the salon's line breaks without enabling any markup interpretation.
function ExpectationCard({ message, className = "" }) {
  const text = (message || "").trim();
  if (!text) return null;
  return (
    <div
      className={`bg-[#FBF7EE] border border-[#E6D9B8] rounded-lg p-4 mb-6 ${className}`}
      data-testid="expectation-card"
    >
      <div className="text-[10px] uppercase tracking-wider font-semibold text-[#8A6A2C]">
        What to expect
      </div>
      <p
        className="text-sm text-[#5A4A1F] mt-1 whitespace-pre-line"
        data-testid="expectation-card-message"
      >
        {text}
      </p>
    </div>
  );
}
